import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  ArrowLeft, Eye, Save, Plus, Trash2, UserPlus, Package,
  CheckCircle2, ChevronDown, Camera
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

// Authenticated thumbnail URL for a line-item photo (img tags can't send headers)
export const itemPhotoSrc = (photoUrl, size = "thumb") => {
  if (!photoUrl) return null;
  const token = localStorage.getItem("token") || "";
  return `${API}${photoUrl.replace(/^\/api/, "")}?size=${size}&auth=${token}`;
};
import SearchableCustomerSelect from "../shared/SearchableCustomerSelect";
import { ItemAutocomplete, ItemPickerModal, QuickSaveItemBtn } from "./ItemsLibrary";

const InvoiceFormDialog = ({
  open,
  onClose,
  customers,
  isEstimate,
  isDark,
  headers,
  onCreated,
  paymentTypes = [],
  jobPrefill = null,
  defaultCompanyName = "",
  editingDoc = null,
  onPreview = null,
}) => {
  const isEditing = !!editingDoc;
  const [docType, setDocType] = useState(isEstimate ? "estimate" : "invoice");
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [customerJobs, setCustomerJobs] = useState([]); // jobs for the currently-selected customer
  const [form, setForm] = useState({
    customer_id: "",
    job_id: null,
    // Multi-location: which store location is this doc for? Null = company-wide.
    // For single-location accounts, this stays null and behaves exactly
    // like before. For multi-location accounts, the user picks one.
    location_id: null,
    items: [{ description: "", quantity: 1, unit_price: 0, discount: 0 }],
    tax_rate: 0,
    discount_type: "percentage",
    discount_value: 0,
    credit_fee_enabled: false,
    credit_fee_type: "percentage",
    credit_fee_value: 0,
    due_date: "",
    valid_until: "",
    notes: "",
    payment_terms: "",
    deposit_required: "",
    company_name: defaultCompanyName,
  });
  const [submitting, setSubmitting] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  // Store locations — populated once on open. We only render the picker
  // when there are 2+ locations (single-location accounts get the
  // original UX with zero new clutter).
  const [storeLocations, setStoreLocations] = useState([]);
  const showLocationPicker = storeLocations.length >= 2;
  // True once the user manually types in "Business Name on Document" —
  // brand selection stops auto-overwriting their custom text.
  const companyNameTouchedRef = useRef(false);
  // Automatic location-based tax: when Business Settings enables it, the rate
  // is resolved server-side from the service address (linked job's, else the
  // customer's) and the manual Tax Rate input becomes a read-only display.
  // null = still loading /tax/settings — render a disabled placeholder so a
  // user can't type into the manual input during the brief resolve window.
  const [autoTaxEnabled, setAutoTaxEnabled] = useState(null);
  const [taxPreview, setTaxPreview] = useState(null);
  const taxDebounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/tax/settings`, { headers, params: form.location_id ? { location_id: form.location_id } : {} })
      .then(r => setAutoTaxEnabled(r.data?.charge_tax === true))
      .catch(() => setAutoTaxEnabled(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.location_id]);

  useEffect(() => {
    if (!open || !autoTaxEnabled) { setTaxPreview(null); return; }
    const job = form.job_id ? customerJobs.find(j => j.job_id === form.job_id) : null;
    const cust = (customers || []).find(c => c.customer_id === form.customer_id);
    const body = job && (job.customer_address || job.zip_code)
      ? { address: job.customer_address || "", city: job.address_city || "", state: job.address_state || "", zip_code: job.zip_code || "" }
      : { address: cust?.address || "", city: cust?.city || "", state: cust?.state || "", zip_code: cust?.zip_code || "" };
    clearTimeout(taxDebounceRef.current);
    taxDebounceRef.current = setTimeout(() => {
      axios.post(`${API}/tax/preview`, {
        ...body, amount: 0,
        customer_id: form.customer_id || null, location_id: form.location_id || null,
      }, { headers })
        .then(r => {
          setTaxPreview(r.data);
          const st = r.data?.status;
          if (st === "calculated" || st === "zero_tax_jurisdiction") {
            const rate = r.data.rate || 0;
            setForm(f => (f.tax_rate === rate ? f : { ...f, tax_rate: rate }));
          } else if (st === "tax_exempt") {
            setForm(f => (f.tax_rate === 0 ? f : { ...f, tax_rate: 0 }));
          }
        })
        .catch(() => setTaxPreview(null));
    }, 500);
    return () => clearTimeout(taxDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoTaxEnabled, form.customer_id, form.job_id, form.location_id, customerJobs]);

  // Load invoice settings defaults
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/invoices/template/settings`, { headers }).then(res => {
      setInvoiceSettings(res.data);
    }).catch(() => {});
    // Fetch store locations once per open. If the account has fewer than
    // 2 we'll silently hide the picker (showLocationPicker derived above).
    axios.get(`${API}/store/locations`, { headers }).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.locations || []);
      setStoreLocations(list);
    }).catch(() => setStoreLocations([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    companyNameTouchedRef.current = false;
    if (editingDoc) {
      setDocType(editingDoc.document_type || "invoice");
      setForm({
        customer_id: editingDoc.customer_id || "",
        job_id: editingDoc.job_id || null,
        location_id: editingDoc.location_id || null,
        items: editingDoc.items?.length > 0
          ? editingDoc.items.map((it) => ({ long_description: "", ...it }))
          : [{ description: "", long_description: "", quantity: 1, unit_price: 0, discount: 0 }],
        tax_rate: editingDoc.tax_rate || 0,
        discount_type: editingDoc.discount_type || "percentage",
        discount_value: editingDoc.discount_value ?? (editingDoc.discount_type === "flat" ? (editingDoc.discount_total || 0) : 0),
        credit_fee_enabled: editingDoc.credit_fee_enabled || false,
        credit_fee_type: editingDoc.credit_fee_type || "percentage",
        credit_fee_value: editingDoc.credit_fee_value || 0,
        due_date: editingDoc.due_date ? new Date(editingDoc.due_date).toISOString().split('T')[0] : "",
        valid_until: editingDoc.valid_until ? new Date(editingDoc.valid_until).toISOString().split('T')[0] : "",
        notes: editingDoc.notes || "",
        payment_terms: editingDoc.payment_terms || "",
        deposit_required: editingDoc.deposit_required ? String(editingDoc.deposit_required) : "",
        company_name: editingDoc.company_name || defaultCompanyName,
      });
      setMarkAsPaid(false);
      setPaymentMethod("");
    } else if (jobPrefill) {
      setDocType(jobPrefill.docType || "invoice");
      const s = invoiceSettings || {};
      setForm({
        customer_id: jobPrefill.customer_id || "",
        job_id: jobPrefill.job_id || null,
        // Inherit the job's location so the invoice/estimate stays scoped
        // to the same site without the user having to re-pick.
        location_id: jobPrefill.location_id || jobPrefill.store_location_id || null,
        items: jobPrefill.items?.length > 0
          ? jobPrefill.items.map((it) => ({ long_description: "", ...it }))
          : [{ description: "", long_description: "", quantity: 1, unit_price: 0, discount: 0 }],
        tax_rate: s.default_tax_rate || 0,
        discount_type: "percentage",
        discount_value: 0,
        credit_fee_enabled: s.default_credit_fee_enabled || false,
        credit_fee_type: s.default_credit_fee_type || "percentage",
        credit_fee_value: s.default_credit_fee_value || 0,
        due_date: "", valid_until: "", notes: s.default_notes || "", payment_terms: "",
        deposit_required: "",
        company_name: defaultCompanyName,
      });
      setMarkAsPaid(false);
      setPaymentMethod("");
    } else {
      setDocType(isEstimate ? "estimate" : "invoice");
      const s = invoiceSettings || {};
      setForm({
        customer_id: "", job_id: null,
        location_id: null,  // seeded by the locations-arrived useEffect below
        items: [{ description: "", long_description: "", quantity: 1, unit_price: 0, discount: 0 }],
        tax_rate: s.default_tax_rate || 0,
        discount_type: "percentage",
        discount_value: 0,
        credit_fee_enabled: s.default_credit_fee_enabled || false,
        credit_fee_type: s.default_credit_fee_type || "percentage",
        credit_fee_value: s.default_credit_fee_value || 0,
        due_date: "", valid_until: "", notes: s.default_notes || "", payment_terms: "",
        deposit_required: "",
        company_name: defaultCompanyName,
      });
      setMarkAsPaid(false);
      setPaymentMethod("");
    }
  }, [open, isEstimate, jobPrefill, defaultCompanyName, editingDoc, invoiceSettings]);

  // Once locations arrive (async), if the form has no location yet and
  // we're not editing/job-prefilling, default to the account's default
  // location. Runs at most once per dialog open.
  useEffect(() => {
    if (!open) return;
    if (editingDoc || jobPrefill) return; // those branches already set location
    if (form.location_id) return; // user already picked
    if (storeLocations.length === 0) return;
    const defaultLoc = storeLocations.find(l => l.is_default) || storeLocations[0];
    if (defaultLoc) {
      setForm(prev => ({ ...prev, location_id: defaultLoc.location_id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storeLocations, editingDoc, jobPrefill]);

  // Per-location default notes: when a NEW doc's location changes, swap the
  // notes to that location's default — but never overwrite user-typed text.
  useEffect(() => {
    if (!open || editingDoc) return;
    const s = invoiceSettings || {};
    const eff = (form.location_id && s.location_overrides?.[form.location_id]?.default_notes) || s.default_notes || "";
    setForm((f) => {
      if (f.notes === eff) return f;
      const knownDefaults = new Set([
        "", s.default_notes || "",
        ...Object.values(s.location_overrides || {}).map((o) => o?.default_notes || ""),
      ]);
      return knownDefaults.has(f.notes) ? { ...f, notes: eff } : f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingDoc, invoiceSettings, form.location_id]);


  // Whenever the picked business/brand changes (and we're not editing an
  // existing doc), pull that brand's per-location tax_rate AND sync the
  // "Business Name on Document" to the brand's business name so the invoice
  // is visibly (and on the PDF/email) from the selected brand. Reverting to
  // "Main business" restores the default company name. A manually-typed
  // name is never overwritten.
  useEffect(() => {
    if (!open || editingDoc) return;
    const loc = form.location_id
      ? storeLocations.find(l => l.location_id === form.location_id)
      : null;
    if (loc && typeof loc.tax_rate === "number" && !autoTaxEnabled) {
      setForm(prev => ({ ...prev, tax_rate: loc.tax_rate }));
    }
    if (!companyNameTouchedRef.current) {
      const brandName = (loc?.business_name || "").trim() || defaultCompanyName;
      setForm(prev => (prev.company_name === brandName ? prev : { ...prev, company_name: brandName }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.location_id, storeLocations]);

  // Fetch jobs for the selected customer so the user can attach the doc to a job
  useEffect(() => {
    if (!open || !form.customer_id) {
      setCustomerJobs([]);
      return;
    }
    let active = true;
    axios.get(`${API}/jobs?customer_id=${form.customer_id}&limit=50`, { headers })
      .then((r) => {
        if (!active) return;
        const list = Array.isArray(r.data) ? r.data : (r.data?.jobs || []);
        setCustomerJobs(list);
      })
      .catch(() => active && setCustomerJobs([]));
    return () => { active = false; };
  }, [open, form.customer_id, headers]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: "", long_description: "", quantity: 1, unit_price: 0, discount: 0 }] }));
  const removeItem = (i) => { if (form.items.length > 1) setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); };
  const updateItem = (i, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [field]: value };
      return { ...f, items };
    });
  };
  // Batch-set several fields on one line item in a single state update —
  // used by the saved-item autocomplete so name/price/qty land together.
  const updateItemFields = (i, fields) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], ...fields };
      return { ...f, items };
    });
  };

  const [uploadingPhotoIdx, setUploadingPhotoIdx] = useState(null);
  const uploadItemPhoto = async (i, file) => {
    setUploadingPhotoIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/invoices/item-photos`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      updateItem(i, "photo_url", res.data.photo_url);
      toast.success("Item photo added — it will show on the PDF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingPhotoIdx(null);
    }
  };

  const subtotal = form.items.reduce((s, it) => s + it.quantity * it.unit_price, 0);
  const itemDiscountTotal = form.items.reduce((s, it) => s + (it.quantity * it.unit_price * (it.discount || 0) / 100), 0);
  // Taxable vs non-taxable split (per-item `taxable` flag; missing = taxable —
  // matches backend core/tax.compute_document_totals exactly).
  let taxableAfterItem = 0, nonTaxableAfterItem = 0;
  for (const it of form.items) {
    const line = it.quantity * it.unit_price;
    const after = line - line * (it.discount || 0) / 100;
    if (it.taxable === false) nonTaxableAfterItem += after; else taxableAfterItem += after;
  }
  const afterItemDiscounts = Math.max(taxableAfterItem + nonTaxableAfterItem, 0);
  // Main document-level discount — % of the (item-discounted) subtotal or a flat $.
  const mainDiscountAmount = form.discount_type === "flat"
    ? Math.min(form.discount_value || 0, afterItemDiscounts)
    : round2(afterItemDiscounts * ((form.discount_value || 0) / 100));
  const discountTotal = round2(itemDiscountTotal + mainDiscountAmount);
  const afterDiscount = subtotal - discountTotal;
  // Main discount is allocated proportionally between taxable / non-taxable lines.
  const taxableShare = afterItemDiscounts > 0 ? taxableAfterItem / afterItemDiscounts : 0;
  const taxableBase = Math.max(taxableAfterItem - mainDiscountAmount * taxableShare, 0);
  const taxAmount = round2(taxableBase * (form.tax_rate / 100));
  const afterTax = afterDiscount + taxAmount;
  const creditFeeAmount = form.credit_fee_enabled
    ? round2(form.credit_fee_type === "percentage" ? afterTax * (form.credit_fee_value / 100) : (form.credit_fee_value || 0))
    : 0;
  const total = round2(afterTax + creditFeeAmount);

  function round2(v) { return Math.round(v * 100) / 100; }

  // Custom business-name override: when the typed name differs from the
  // brand/default name, the saved doc hides all other business identity
  // (logo, address, phone, email) on every customer-facing surface.
  const overrideBrandLoc = form.location_id ? storeLocations.find(l => l.location_id === form.location_id) : null;
  const brandDefaultName = ((overrideBrandLoc?.business_name || "").trim()) || (defaultCompanyName || "").trim();
  const typedName = (form.company_name || "").trim();
  const isNameOverridden = !!typedName && typedName.toLowerCase() !== brandDefaultName.toLowerCase();

  const handleSubmit = async () => {
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!form.items[0]?.description) { toast.error("Add at least one item"); return; }
    if (markAsPaid && docType === "invoice" && !paymentMethod) { toast.error("Select payment method"); return; }

    setSubmitting(true);
    try {
      let savedDoc = null;
      if (isEditing) {
        const res = await axios.put(`${API}/invoices/${editingDoc.invoice_id}`, {
          items: form.items,
          subtotal, discount_total: round2(discountTotal), discount_type: form.discount_type,
          discount_value: form.discount_value || 0,
          tax_rate: form.tax_rate, tax_amount: taxAmount,
          credit_fee_enabled: form.credit_fee_enabled, credit_fee_type: form.credit_fee_type,
          credit_fee_value: form.credit_fee_value, credit_fee_amount: creditFeeAmount,
          total,
          due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
          valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
          notes: form.notes, payment_terms: form.payment_terms, customer_id: form.customer_id,
          deposit_required: form.deposit_required !== "" ? (parseFloat(form.deposit_required) || 0) : null,
          location_id: form.location_id || null,
          company_name: form.company_name || null,
          company_name_override: isNameOverridden,
        }, { headers });
        savedDoc = res.data;
        toast.success(`${docType === "estimate" ? "Estimate" : "Invoice"} updated!`);
      } else {
        const payload = {
          ...form, document_type: docType,
          subtotal, discount_total: round2(discountTotal), discount_type: form.discount_type,
          tax_amount: taxAmount,
          credit_fee_enabled: form.credit_fee_enabled, credit_fee_type: form.credit_fee_type,
          credit_fee_value: form.credit_fee_value, credit_fee_amount: creditFeeAmount,
          total,
          due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
          valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
          deposit_required: form.deposit_required !== "" ? (parseFloat(form.deposit_required) || 0) : null,
          company_name: form.company_name || null,
          company_name_override: isNameOverridden,
        };
        if (markAsPaid && docType === "invoice") {
          payload.status = "paid";
          payload.payment_method = paymentMethod;
        }
        const res = await axios.post(`${API}/invoices`, payload, { headers });
        savedDoc = res.data;
        toast.success(`${docType === "estimate" ? "Estimate" : "Invoice"} created${markAsPaid && docType === "invoice" ? " as paid" : ""}!`);
      }
      onClose();
      onCreated(savedDoc);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${isEditing ? "update" : "create"}`);
    } finally { setSubmitting(false); }
  };

  const isInvoice = docType === "invoice";
  const accent = isInvoice ? "#3b82f6" : "#f97316";
  const docLabel = isEditing
    ? editingDoc.invoice_number
    : isInvoice ? "New Invoice" : "New Estimate";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 max-h-[95vh] sm:max-h-[95vh] h-[100dvh] sm:h-auto overflow-hidden flex flex-col [&>button.absolute]:hidden" data-testid="invoice-form-dialog">
        <DialogTitle className="sr-only">{docLabel}</DialogTitle>
        <DialogDescription className="sr-only">Create or edit an invoice or estimate</DialogDescription>
        {/* Top bar */}
        <div className={`flex items-center justify-between px-4 sm:px-5 py-3 border-b shrink-0 ${isDark ? "border-zinc-800" : "border-gray-100"}`}>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 -ml-1" data-testid="form-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className={`text-sm font-semibold truncate mx-3 ${isDark ? "text-zinc-300" : "text-gray-700"}`}>{docLabel}</span>
          <div className="flex items-center gap-1 shrink-0">
            <button className="text-xs sm:text-sm font-medium px-2.5 py-1.5 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5" style={{ color: accent }} data-testid="form-preview-btn"
              onClick={() => {
                const selectedCust = customers.find(c => c.customer_id === form.customer_id);
                return onPreview && onPreview({
                  ...form, document_type: docType, subtotal,
                  company_name_override: isNameOverridden,
                  discount_total: round2(discountTotal), discount_type: form.discount_type,
                  tax_rate: form.tax_rate, tax_amount: taxAmount,
                  taxable_subtotal: round2(taxableBase),
                  non_taxable_subtotal: round2(Math.max(nonTaxableAfterItem - mainDiscountAmount * (1 - taxableShare), 0)),
                  credit_fee_enabled: form.credit_fee_enabled, credit_fee_type: form.credit_fee_type,
                  credit_fee_value: form.credit_fee_value, credit_fee_amount: creditFeeAmount,
                  total,
                  invoice_number: editingDoc?.invoice_number || (isInvoice ? "INV-XXXXX" : "EST-XXXXX"),
                  customer_name: selectedCust?.name || "Customer",
                  // Include the full address block so the preview matches the
                  // saved/sent invoice — street + city, state and zip.
                  customer_address: selectedCust?.address || "",
                  customer_city: selectedCust?.city || "",
                  customer_state: selectedCust?.state || "",
                  customer_zip: selectedCust?.zip_code || "",
                  created_at: new Date().toISOString(), status: "draft",
                });
              }}>
              <Eye className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline">Preview</span>
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors ${submitting ? "opacity-50" : "hover:bg-black/5 dark:hover:bg-white/5"}`}
              style={{ color: accent }}
              data-testid="form-save-btn"
            >
              {submitting ? "..." : "Save"}
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className={`overflow-y-auto flex-1 ${isDark ? "bg-zinc-950" : "bg-gray-50/50"}`}>
          {/* Header section */}
          <div className={`mx-4 mt-4 rounded-xl p-5 ${isDark ? "bg-zinc-900 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"}`}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`} data-testid="form-doc-number">
                  {docLabel}
                </h2>
                {!isEditing && (
                  <button className="text-sm font-medium mt-1" style={{ color: accent }}>
                    Details
                  </button>
                )}
              </div>
              <Badge variant="outline" className={`text-xs ${isDark ? "border-zinc-700 text-zinc-400" : "border-gray-200 text-gray-500"}`}>
                {isInvoice
                  ? form.due_date ? `Due ${form.due_date}` : "Due on receipt"
                  : form.valid_until ? `Valid until ${form.valid_until}` : "No expiry"
                }
              </Badge>
            </div>

            {/* Type + Date row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              {!isEditing && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <Select value={docType} onValueChange={(v) => { setDocType(v); if (v === "estimate") { setMarkAsPaid(false); setPaymentMethod(""); } }}>
                    <SelectTrigger className="h-9" data-testid="create-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="estimate">Estimate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{isInvoice ? "Due Date" : "Valid Until"}</Label>
                <Input
                  type="date"
                  value={isInvoice ? form.due_date : form.valid_until}
                  onChange={(e) => setForm(f => ({ ...f, [isInvoice ? "due_date" : "valid_until"]: e.target.value }))}
                  className="h-9 text-sm"
                  data-testid="create-date"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Tax Rate (%)</Label>
                {autoTaxEnabled === null ? (
                  <Input disabled value={form.tax_rate} className="h-9 text-sm opacity-60" data-testid="create-tax-loading" />
                ) : autoTaxEnabled ? (
                  <div
                    className={`h-9 px-3 flex items-center justify-between rounded-md border text-sm ${isDark ? "border-zinc-700 bg-zinc-900 text-zinc-200" : "border-gray-200 bg-gray-50 text-gray-700"}`}
                    data-testid="create-tax-auto"
                  >
                    <span className="font-medium">{taxPreview?.status === "tax_exempt" ? "Exempt" : `${form.tax_rate}%`}</span>
                    <span className="text-[9px] uppercase tracking-wide font-semibold text-teal-500">Auto</span>
                  </div>
                ) : (
                  <Input type="number" step="0.1" min="0" max="100" value={form.tax_rate} onChange={(e) => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))} className="h-9 text-sm" data-testid="create-tax" />
                )}
              </div>
            </div>

            {/* Automatic tax status line */}
            {autoTaxEnabled && taxPreview && (
              <p
                className={`mt-2 text-[11px] ${["needs_address", "calculation_failed"].includes(taxPreview.status) ? "text-orange-500" : "text-muted-foreground"}`}
                data-testid="create-tax-auto-note"
              >
                {taxPreview.status === "needs_address"
                  ? "Tax needs a complete service address on the customer or linked job (street, city, state/province, ZIP/postal)."
                  : taxPreview.status === "calculation_failed"
                    ? (taxPreview.note || "Unable to verify tax rate — the tax service is unavailable.")
                    : taxPreview.status === "tax_exempt"
                      ? (taxPreview.note || "Tax exempt customer")
                      : taxPreview.jurisdiction
                        ? `Automatic tax — ${taxPreview.jurisdiction}`
                        : "Automatic tax from the service address"}
              </p>
            )}

            {/* Custom Business Name */}
            <div className="mt-3 space-y-1.5">
              <Label className="text-xs text-muted-foreground">Business Name on Document</Label>
              <Input
                value={form.company_name}
                onChange={(e) => {
                  companyNameTouchedRef.current = true;
                  setForm(f => ({ ...f, company_name: e.target.value }));
                }}
                className="h-9 text-sm"
                placeholder={defaultCompanyName || "Company name (leave blank for default)"}
                data-testid="create-company-name"
              />
              {isNameOverridden && (
                <p className="text-[11px] leading-snug text-amber-600 dark:text-amber-500" data-testid="company-name-override-hint">
                  Custom name — your business logo, address, phone &amp; email will be hidden on this document. Customers will only see "{typedName}".
                </p>
              )}
            </div>
          </div>

          {/* Client section */}
          <div className={`mx-4 mt-3 rounded-xl p-5 ${isDark ? "bg-zinc-900 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"}`}>
            {/* Location picker — only visible for accounts with 2+ store
                locations. Single-location accounts see no extra clutter. */}
            {showLocationPicker && (
              <div className="mb-4">
                <Label className={`text-xs uppercase tracking-wide font-medium ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                  Business / Brand
                </Label>
                <Select
                  value={form.location_id || "__none"}
                  onValueChange={(v) => setForm(f => ({ ...f, location_id: v === "__none" ? null : v }))}
                >
                  <SelectTrigger className="h-9 text-sm mt-2" data-testid="create-location">
                    <SelectValue placeholder="Select business / brand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Main business (default)</SelectItem>
                    {storeLocations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}{loc.industry ? ` — ${loc.industry}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Label className={`text-xs uppercase tracking-wide font-medium ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Client</Label>
            <div className="mt-2">
              <SearchableCustomerSelect
                customers={customers}
                value={form.customer_id}
                onChange={(v) => setForm(f => ({ ...f, customer_id: v, job_id: null }))}
                placeholder="Select customer"
                testId="create-customer"
              />
            </div>

            {/* Job picker — appears only when a customer is selected and they have jobs */}
            {form.customer_id && (
              <div className="mt-3">
                <Label className={`text-xs uppercase tracking-wide font-medium ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                  Linked Job {customerJobs.length === 0 && <span className="text-[10px] normal-case ml-1 text-muted-foreground">(no jobs for this customer)</span>}
                </Label>
                <Select
                  value={form.job_id || "__none"}
                  onValueChange={(v) => setForm(f => ({ ...f, job_id: v === "__none" ? null : v }))}
                  disabled={isEditing}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="invoice-job-select">
                    <SelectValue placeholder="Optional — link to a job" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— No job —</SelectItem>
                    {customerJobs.map((j) => (
                      <SelectItem key={j.job_id} value={j.job_id}>
                        {j.title || j.service_name || "Job"}
                        {j.ticket_number && <span className="text-muted-foreground text-[10px] ml-2">#{j.ticket_number}</span>}
                        {j.created_at && <span className="text-muted-foreground text-[10px] ml-2">· {new Date(j.created_at).toLocaleDateString()}</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!isEditing && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Linking this {docType} to a job lets you find both together later from the job's detail view.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Items section */}
          <div className={`mx-4 mt-3 rounded-xl p-5 ${isDark ? "bg-zinc-900 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"}`}>
            <div className="flex items-center justify-between mb-3">
              <Label className={`text-xs uppercase tracking-wide font-medium ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Items</Label>
              <Button type="button" variant="ghost" size="sm" onClick={() => setItemPickerOpen(true)} className="text-xs h-7 text-muted-foreground hover:text-foreground" data-testid="browse-items-btn">
                <Package className="w-3 h-3 mr-1" /> Browse
              </Button>
            </div>

            <div className="space-y-2.5">
              {form.items.map((item, i) => {
                const lineDiscount = item.quantity * item.unit_price * (item.discount || 0) / 100;
                const lineTotal = item.quantity * item.unit_price - lineDiscount;
                return (
                  <div key={i} className={`rounded-lg p-3 ${isDark ? "bg-zinc-800/50" : "bg-gray-50"}`} data-testid={`item-row-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-2">
                        <ItemAutocomplete
                          value={item.description}
                          onChange={(val) => updateItem(i, "description", val)}
                          onSelect={(saved) => {
                            updateItemFields(i, {
                              description: saved.name || "",
                              long_description: saved.description || "",
                              unit_price: saved.unit_price || 0,
                              quantity: saved.default_quantity || 1,
                              discount: saved.discount || 0,
                              ...(saved.photo_url ? { photo_url: saved.photo_url } : {}),
                            });
                            if (saved.tax_rate !== undefined && saved.tax_rate !== null && saved.tax_rate !== "") {
                              setForm(f => ({ ...f, tax_rate: parseFloat(saved.tax_rate) || f.tax_rate }));
                            }
                          }}
                          isDark={isDark}
                          headers={headers}
                        />
                        {/* Long description / details — optional. Surfaced
                            on the saved doc, the preview, and the exported
                            PDF below the short title (multi-line). */}
                        <Textarea
                          value={item.long_description || ""}
                          onChange={(e) => updateItem(i, "long_description", e.target.value)}
                          placeholder="Add a longer description (optional) — e.g. lock model, warranty, scope of work…"
                          rows={2}
                          className={`text-sm resize-y min-h-[44px] ${isDark ? "bg-zinc-900 border-zinc-700 text-zinc-200 placeholder:text-zinc-500" : "bg-white border-gray-200"}`}
                          data-testid={`item-long-description-${i}`}
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <span className={`text-[10px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Qty</span>
                            <Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(i, "quantity", parseInt(e.target.value) || 1)} className="h-8 text-sm" data-testid={`item-qty-${i}`} />
                          </div>
                          <div>
                            <span className={`text-[10px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Price</span>
                            <Input type="number" step="0.01" min="0" value={item.unit_price} onChange={(e) => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)} className="h-8 text-sm" data-testid={`item-price-${i}`} />
                          </div>
                          <div>
                            <span className={`text-[10px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Disc %</span>
                            <Input type="number" step="1" min="0" max="100" value={item.discount} onChange={(e) => updateItem(i, "discount", parseFloat(e.target.value) || 0)} className="h-8 text-sm" />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => updateItem(i, "taxable", item.taxable === false)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                            item.taxable === false
                              ? isDark ? "border-zinc-600 text-zinc-400 hover:border-zinc-500" : "border-gray-300 text-gray-500 hover:border-gray-400"
                              : "border-teal-500/50 text-teal-600 bg-teal-500/10 hover:bg-teal-500/20"
                          }`}
                          title="Toggle whether sales tax applies to this line item"
                          data-testid={`item-taxable-toggle-${i}`}
                        >
                          {item.taxable === false ? "Non-taxable" : "Taxable"}
                        </button>
                        {/* Item photo — shown on the app view + embedded in the PDF */}
                        <div className="flex items-center gap-2 mt-1">
                          {item.photo_url ? (
                            <div className="relative inline-block" data-testid={`item-photo-wrap-${i}`}>
                              <img
                                src={itemPhotoSrc(item.photo_url)}
                                alt="Item"
                                className={`w-11 h-11 rounded-md object-cover border ${isDark ? "border-zinc-700" : "border-gray-200"}`}
                                data-testid={`item-photo-thumb-${i}`}
                              />
                              <button
                                type="button"
                                onClick={() => updateItem(i, "photo_url", null)}
                                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center"
                                title="Remove photo"
                                data-testid={`item-photo-remove-${i}`}
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <label
                              className={`cursor-pointer text-[10px] font-medium px-2 py-0.5 rounded-full border flex items-center gap-1 transition-colors ${
                                uploadingPhotoIdx === i
                                  ? "opacity-60 pointer-events-none border-zinc-500 text-zinc-400"
                                  : isDark ? "border-zinc-600 text-zinc-400 hover:border-zinc-400 hover:text-zinc-200" : "border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700"
                              }`}
                              title="Attach a photo to this item — it will appear on the PDF"
                            >
                              <Camera className="w-3 h-3" />
                              {uploadingPhotoIdx === i ? "Uploading…" : "Photo"}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) uploadItemPhoto(i, f);
                                  e.target.value = "";
                                }}
                                data-testid={`item-photo-input-${i}`}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 pt-1">
                        <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>${lineTotal.toFixed(2)}</span>
                        <QuickSaveItemBtn item={item} isDark={isDark} headers={headers} />
                        {form.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-500 p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add item button - InvoiceFly style */}
            <button
              type="button"
              onClick={addItem}
              className="w-full mt-3 py-2.5 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90"
              style={{ backgroundColor: accent }}
              data-testid="add-item-btn"
            >
              <Plus className="w-4 h-4" />
              Add item
            </button>
          </div>

          {/* Totals section */}
          <div className={`mx-4 mt-3 rounded-xl p-5 ${isDark ? "bg-zinc-900 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"}`}>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className={isDark ? "text-zinc-400" : "text-gray-500"}>Subtotal</span>
                <span className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>${subtotal.toFixed(2)}</span>
              </div>

              {/* Per-item discounts (from line-item Disc % fields) */}
              {itemDiscountTotal > 0 && (
                <div className="flex justify-between text-sm text-orange-500">
                  <span>Per-item discounts</span>
                  <span>-${itemDiscountTotal.toFixed(2)}</span>
                </div>
              )}

              {/* Main document-level discount — % or flat $ */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${isDark ? "text-zinc-400" : "text-gray-500"}`}>Discount</span>
                    <div className={`flex rounded-md overflow-hidden text-[10px] font-medium border ${isDark ? "border-zinc-700" : "border-gray-200"}`}>
                      <button type="button" onClick={() => setForm(f => ({ ...f, discount_type: "percentage", discount_value: 0 }))}
                        className={`px-2 py-0.5 transition-colors ${form.discount_type === "percentage" ? "bg-blue-500 text-white" : isDark ? "text-zinc-400" : "text-gray-500"}`}
                        data-testid="discount-type-pct">%</button>
                      <button type="button" onClick={() => setForm(f => ({ ...f, discount_type: "flat", discount_value: 0 }))}
                        className={`px-2 py-0.5 transition-colors ${form.discount_type === "flat" ? "bg-blue-500 text-white" : isDark ? "text-zinc-400" : "text-gray-500"}`}
                        data-testid="discount-type-flat">$</button>
                    </div>
                  </div>
                  {mainDiscountAmount > 0 && (
                    <span className="text-sm text-orange-500" data-testid="main-discount-amount">-${mainDiscountAmount.toFixed(2)}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" step="0.01" min="0"
                    max={form.discount_type === "percentage" ? 100 : afterItemDiscounts}
                    value={form.discount_value}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value) || 0;
                      setForm(f => ({
                        ...f,
                        discount_value: f.discount_type === "percentage"
                          ? Math.min(v, 100)
                          : Math.min(v, afterItemDiscounts),
                      }));
                    }}
                    className="h-8 text-sm w-28"
                    placeholder={form.discount_type === "percentage" ? "% off total" : "$ off total"}
                    data-testid="main-discount-input"
                  />
                  <span className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
                    {form.discount_type === "percentage" ? "% off entire document" : "$ off entire document"}
                  </span>
                </div>
              </div>

              {form.tax_rate > 0 && nonTaxableAfterItem > 0 && (
                <div className="flex justify-between text-sm" data-testid="taxable-subtotal-row">
                  <span className={isDark ? "text-zinc-400" : "text-gray-500"}>Taxable subtotal</span>
                  <span className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>${taxableBase.toFixed(2)}</span>
                </div>
              )}

              {form.tax_rate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className={isDark ? "text-zinc-400" : "text-gray-500"}>Tax ({form.tax_rate}%)</span>
                  <span className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>${taxAmount.toFixed(2)}</span>
                </div>
              )}

              {/* Credit Fee */}
              {(invoiceSettings?.allow_invoice_fee_override !== false || form.credit_fee_enabled) && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${isDark ? "text-zinc-400" : "text-gray-500"}`}>Credit Fee</span>
                      <div className={`flex rounded-md overflow-hidden text-[10px] font-medium border ${isDark ? "border-zinc-700" : "border-gray-200"}`}>
                        <button type="button" onClick={() => setForm(f => ({ ...f, credit_fee_type: "percentage" }))}
                          className={`px-2 py-0.5 transition-colors ${form.credit_fee_type === "percentage" ? "bg-blue-500 text-white" : isDark ? "text-zinc-400" : "text-gray-500"}`}
                          data-testid="fee-type-pct">%</button>
                        <button type="button" onClick={() => setForm(f => ({ ...f, credit_fee_type: "flat" }))}
                          className={`px-2 py-0.5 transition-colors ${form.credit_fee_type === "flat" ? "bg-blue-500 text-white" : isDark ? "text-zinc-400" : "text-gray-500"}`}
                          data-testid="fee-type-flat">$</button>
                      </div>
                    </div>
                    <Switch checked={form.credit_fee_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, credit_fee_enabled: v }))} data-testid="credit-fee-toggle" />
                  </div>
                  {form.credit_fee_enabled && (
                    <div className="flex items-center gap-2">
                      <Input type="number" step="0.01" min="0" value={form.credit_fee_value}
                        onChange={(e) => setForm(f => ({ ...f, credit_fee_value: parseFloat(e.target.value) || 0 }))}
                        className="h-8 text-sm w-28" placeholder={form.credit_fee_type === "percentage" ? "%" : "$"}
                        data-testid="credit-fee-value" />
                      <span className={`text-sm ${isDark ? "text-zinc-300" : "text-gray-700"}`}>+${creditFeeAmount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className={`flex justify-between pt-2.5 mt-1 border-t ${isDark ? "border-zinc-800" : "border-gray-200"}`}>
                <span className={`text-base font-bold ${isDark ? "text-white" : "text-gray-900"}`}>Total</span>
                <span className={`text-base font-bold ${isDark ? "text-white" : "text-gray-900"}`}>${total.toFixed(2)}</span>
              </div>

              {/* Deposit required (invoices only) — drives the customer
                  payment page's "Pay deposit" option */}
              {isInvoice && (
                <div className="flex items-center justify-between pt-1">
                  <div>
                    <span className={`text-sm ${isDark ? "text-zinc-400" : "text-gray-500"}`}>Deposit required</span>
                    <p className={`text-[10px] ${isDark ? "text-zinc-600" : "text-gray-400"}`}>Optional — e.g. to order parts</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-sm ${isDark ? "text-zinc-500" : "text-gray-400"}`}>$</span>
                    <Input
                      type="number" step="0.01" min="0"
                      value={form.deposit_required}
                      onChange={(e) => setForm(f => ({ ...f, deposit_required: e.target.value }))}
                      placeholder="0.00"
                      className="h-8 text-sm w-28 text-right"
                      data-testid="deposit-required-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mark as Paid toggle */}
          {isInvoice && !isEditing && (
            <div className={`mx-4 mt-3 rounded-xl p-4 ${
              markAsPaid
                ? isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200"
                : isDark ? "bg-zinc-900 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"
            }`} data-testid="mark-paid-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${markAsPaid ? "text-emerald-500" : "text-muted-foreground"}`} />
                  <Label className="text-sm font-medium cursor-pointer" htmlFor="mark-paid-toggle">Mark as Paid</Label>
                </div>
                <Switch id="mark-paid-toggle" checked={markAsPaid} onCheckedChange={setMarkAsPaid} data-testid="mark-paid-toggle" />
              </div>
              {markAsPaid && (
                <div className="mt-3 space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                  <Label className="text-xs text-muted-foreground">Payment Method *</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="h-9" data-testid="payment-method-select"><SelectValue placeholder="Select payment method" /></SelectTrigger>
                    <SelectContent>
                      {paymentTypes.map(pt => (
                        <SelectItem key={pt.payment_id} value={pt.name}>{pt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Notes section */}
          <div className={`mx-4 mt-3 mb-4 rounded-xl p-5 ${isDark ? "bg-zinc-900 border border-zinc-800" : "bg-white border border-gray-100 shadow-sm"}`}>
            <Label className={`text-xs uppercase tracking-wide font-medium ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Comments will appear at the bottom of your invoice"
              className="mt-2 min-h-[80px] text-sm resize-none"
              data-testid="create-notes"
            />
            <div className="mt-3">
              <Label className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>Payment Terms</Label>
              <Input
                value={form.payment_terms}
                onChange={(e) => setForm(f => ({ ...f, payment_terms: e.target.value }))}
                className="h-9 text-sm mt-1"
                placeholder="Net 30, Due on receipt..."
                data-testid="create-terms"
              />
            </div>
          </div>
        </div>

        {/* Item Picker Modal */}
        <ItemPickerModal
          open={itemPickerOpen}
          onOpenChange={setItemPickerOpen}
          isDark={isDark}
          headers={headers}
          onSelect={(selectedItems) => {
            setForm(f => ({
              ...f,
              items: [...f.items.filter(item => item.description.trim()), ...selectedItems]
            }));
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceFormDialog;

